import { useEffect, useRef } from 'react';

/**
 * A tiny pub/sub bus that lets a Copilot action finish a task *inside* a page
 * it just navigated to — e.g. open the CSV-import modal, start an export, or
 * open the rule builder. Those live in each page's local state, so the action
 * dispatches a command and the page subscribes to it.
 *
 * Because navigation mounts the target page asynchronously, a command with no
 * live subscriber is BUFFERED for a few seconds and delivered as soon as the
 * page mounts and subscribes. Same-page dispatches fire immediately.
 */

// Canonical command names — shared contract between actions and pages.
export const CopilotCommand = {
  KnowledgeOpenModal: 'knowledge:open-modal', // payload: 'add' | 'csv' | 'sheet'
  KnowledgeReindex: 'knowledge:reindex',
  LeadsExport: 'leads:export',
  AutomationsAddRule: 'automations:add-rule',
} as const;

type Handler = (payload?: unknown) => void;

const handlers = new Map<string, Set<Handler>>();

interface Pending {
  command: string;
  payload: unknown;
  expiresAt: number;
}
let pending: Pending[] = [];
const BUFFER_MS = 4000;

/** Fire a command. If nobody is listening yet, buffer it for the mounting page. */
export function dispatchCopilotCommand(command: string, payload?: unknown): void {
  const set = handlers.get(command);
  if (set && set.size > 0) {
    set.forEach((h) => h(payload));
    return;
  }
  pending.push({ command, payload, expiresAt: Date.now() + BUFFER_MS });
}

function drainInto(command: string, handler: Handler): void {
  const now = Date.now();
  const keep: Pending[] = [];
  for (const p of pending) {
    if (p.expiresAt < now) continue; // drop expired
    if (p.command === command) handler(p.payload);
    else keep.push(p);
  }
  pending = keep;
}

function subscribe(command: string, handler: Handler): () => void {
  let set = handlers.get(command);
  if (!set) {
    set = new Set();
    handlers.set(command, set);
  }
  set.add(handler);
  drainInto(command, handler); // deliver anything buffered before we mounted
  return () => {
    set?.delete(handler);
  };
}

/**
 * Subscribe a page to a Copilot command for the lifetime of the component.
 * The handler may change every render without re-subscribing.
 */
export function useCopilotCommand(command: string, handler: Handler): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe(command, (payload) => ref.current(payload)), [command]);
}
